import React, {useContext, useEffect} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {CarouselWidgetsContext} from 'components/CarouselWidgets/context/CarouselWidgetsContext'
import {HighlightedTitle} from 'components/Home/HighlightedTitle'
import {MinimalHotelCard} from 'components/MinimalHotelCard'
import {useNavigateToSearchWithTrigger} from 'hooks/useNavigateToSearch'
import {getIsRtlLanguage} from 'modules/meta/selectors'
import {SearchTrigger} from 'modules/searchBox/types'
import {pick} from 'ramda'

import {useDeviceLayout} from '@vio/atlas/context/deviceLayout'
import {ImagePlaceholder} from '@vio/atlas/helpers/ImagePlaceholder'
import {SmoothScrollNavigation} from '@vio/atlas/helpers/SmoothScrollNavigation'
import {trackEvent} from '@vio/core/src/analytics/modules/actions'
import {
  Action,
  Category,
  Entity,
  Team
} from '@vio/core/src/analytics/types/Events'
import FormattedMessageWrapper from '@vio/core/src/localization/components/FormattedMessage'
import {FlattenSearchHistory} from '@vio/core/src/searchHistory'
import {useIsHydrated} from '@vio/core/src/utils/react/hooks/useIsHydrated'

import {Title} from '../components/Title'
import {DESTINATION_KEYS, ITINERARY_KEYS} from '../utils/constants'
import MapPlaceholder from './MapPlaceholder'

export const RecentSearchHistory = () => {
  const isHydrated = useIsHydrated()
  const dispatch = useDispatch()
  const {isMobile} = useDeviceLayout()
  const isRtlLanguage = useSelector(getIsRtlLanguage)
  const {searchHistory, searchIds} = useContext(CarouselWidgetsContext)
  const navigateToSearch = useNavigateToSearchWithTrigger(
    SearchTrigger.RecentSearches
  )

  useEffect(() => {
    if (searchHistory.length) {
      dispatch(
        trackEvent({
          category: Category.System,
          entity: Entity.Carousel,
          action: Action.Displayed,
          component: 'RecentSearches',
          payload: {
            totalNumber: searchIds?.length,
            searchItem: pick(
              [...DESTINATION_KEYS, ...ITINERARY_KEYS],
              searchHistory
            ),
            searches: searchIds
          },
          team: Team.Retention
        })
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, JSON.stringify(searchHistory)])

  const onClick = (item: FlattenSearchHistory, index: number) => {
    const {hotelId, placeId, checkIn, checkOut, rooms, address, boundingBox} =
      item
    const searchItem = {
      ...(boundingBox && {boundingBox}),
      ...(hotelId && {hotelId}),
      ...(placeId && {placeId}),
      ...(address && {address}),
      checkIn,
      checkOut,
      rooms
    }
    dispatch(
      trackEvent({
        category: Category.User,
        entity: Entity.CarouselCard,
        action: Action.Clicked,
        component: 'RecentSearches',
        payload: {
          totalNumber: searchHistory.length,
          index,
          searchItem
        },
        team: Team.Retention
      })
    )
    // bookingHint=1 (show date confirmation tooltip) is used to open the search box for mobile and date confirmation tooltip on desktop
    // it was added as part of this task: https://app.shortcut.com/findhotel/story/154347/update-landing-experience-for-users-with-no-dates-default-dates-widget-traffic
    navigateToSearch({
      ...searchItem,
      userSearch: 1,
      homeSearch: 1,
      bookingHint: '1'
    })
  }

  if (!isHydrated || !searchHistory.length) {
    return null
  }

  const renderTitle = (item: FlattenSearchHistory) => {
    const {objectType} = item

    switch (objectType) {
      case 'hotel':
        return item.hotelName

      case 'place':
        return item.placeName

      case 'area':
        return (
          <FormattedMessageWrapper id="mapArea" defaultMessage="Map area" />
        )
      default:
        return ''
    }
  }

  return (
    <div>
      <Title variant="titleXL">
        <HighlightedTitle source="RecentSearchHistory" />
      </Title>
      <SmoothScrollNavigation
        isRtlLanguage={isRtlLanguage}
        hasSidePaddings={isMobile}
      >
        {searchHistory.map((item, index) => (
          <MinimalHotelCard
            onClick={() => onClick(item, index)}
            imageUrl={item.imageUrl}
            title={renderTitle(item)}
            checkIn={item.checkIn}
            checkOut={item.checkOut}
            rooms={item.rooms}
            placeHolder={
              item.objectType === 'hotel' ? (
                <ImagePlaceholder type="hotel" />
              ) : (
                <MapPlaceholder />
              )
            }
            testId={`recent-searches-item-${index}`}
            fullWidth={isMobile}
            key={`recent-searches-${item?.historyId}`}
          />
        ))}
      </SmoothScrollNavigation>
    </div>
  )
}
